/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import { Container, Form, Row, Col, Spinner } from "react-bootstrap";
import AwareButton from "../../../components/button";
import Navbar from "../../../components/navbar";
import Layout from "../../../layout";
import { useBetween } from "use-between";
import useApi from "../../useApi";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import "./index.scss";

// Import sub-components
import BasicInfoFields from "./BasicInfoFields";
import PromotionTypeFields from "./PromotionTypeFields";
import CustomerSelection from "./CustomerSelection";
import ProductHierarchySelection from "./ProductHierarchySelection";

const NewContents = ({ edit }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const params = useParams();
    const contentId = params.id ? params.id : null;

    const {
        updating,
        isAdmin,
        setUpdating,
        getAllDetailsOfProducts,
        getContentById,
        getAllCustomer,
        updateOrCreateContent
    } = useBetween(useApi);

    // Form state
    const [title, setTitle] = useState("");
    const [promotionType, setPromotionType] = useState("");
    const [offerStatus, setOfferStatus] = useState("ACTIVE");
    const [description, setDescription] = useState("");
    const [imagePath, setImagePath] = useState("");
    const [contentLink, setContentLink] = useState("");
    const [validFrom, setValidFrom] = useState("");
    const [validTo, setValidTo] = useState("");
    const [customers, setCustomers] = useState([]);
    const [selectedCustomers, setSelectedCustomers] = useState([]);

    // Discount-specific state
    const [discountType, setDiscountType] = useState("PERCENTAGE");
    const [discountValue, setDiscountValue] = useState("");

    // BOGO-specific state
    const [bogoAppliesTo, setBogoAppliesTo] = useState("    ");
    const [buyQuantity, setBuyQuantity] = useState("");
    const [getQuantity, setGetQuantity] = useState("");
    const [bogoOfferType, setBogoOfferType] = useState("FREE");
    const [bogoDiscountValue, setBogoDiscountValue] = useState("");

    // Coupon-specific state
    const [couponCode, setCouponCode] = useState("");
    const [minOrderAmount, setMinOrderAmount] = useState("");
    const [maxDiscountAmount, setMaxDiscountAmount] = useState("");
    const [timesUsable, setTimesUsable] = useState("");
    const [couponDescription, setCouponDescription] = useState("");

    // Cascading selection state
    const [selectedBranches, setSelectedBranches] = useState([]);
    const [selectedBrands, setSelectedBrands] = useState([]);
    const [selectedCategories, setSelectedCategories] = useState([]);
    const [selectedSubCategories, setSelectedSubCategories] = useState([]);
    const [selectedPhenotypes, setSelectedPhenotypes] = useState([]);
    const [selectedProducts, setSelectedProducts] = useState([]);

    // Available options based on API response
    const [productData, setProductData] = useState(null);
    const [availableBranches, setAvailableBranches] = useState([]);
    const [availableBrands, setAvailableBrands] = useState([]);
    const [availableCategories, setAvailableCategories] = useState([]);
    const [availableSubCategories, setAvailableSubCategories] = useState([]);
    const [availablePhenotypes, setAvailablePhenotypes] = useState([]);
    const [availableProducts, setAvailableProducts] = useState([]);

    // Product search state
    const [productSearch, setProductSearch] = useState("");

    const PromotionType = Object.freeze({
        DISCOUNT: "DISCOUNT",
        BOGO: "BOGO",
        COUPON: "COUPON",
        BANNER: "BANNER",
        SPOTLIGHT: "SPOTLIGHT",
    });

    const PromotionTypeLabels = Object.freeze({
        [PromotionType.DISCOUNT]: "Discount",
        [PromotionType.BOGO]: "Buy One Get One (BOGO)",
        [PromotionType.COUPON]: "Coupon Code",
        [PromotionType.BANNER]: "Banner",
        [PromotionType.SPOTLIGHT]: "Spotlight",
    });

    const DiscountType = Object.freeze({
        PERCENTAGE: "PERCENTAGE",
        FLAT: "FLAT",
    });

    const DiscountTypeLabels = Object.freeze({
        [DiscountType.PERCENTAGE]: "Percentage",
        [DiscountType.FLAT]: "Flat Amount",
    });

    const BogoOfferType = Object.freeze({
        FREE: "FREE",
        PERCENTAGE_OFF: "PERCENTAGE_OFF",
        FLAT_OFF: "FLAT_OFF",
    });

    const BogoOfferTypeLabels = Object.freeze({
        [BogoOfferType.FREE]: "Get Free",
        [BogoOfferType.PERCENTAGE_OFF]: "Get at % Off",
        [BogoOfferType.FLAT_OFF]: "Get at $ Off",
    });

    const BogoAppliesTo = Object.freeze({
        PRODUCT: "PRODUCT",
        ITEM: "ITEM",
    });

    const BogoAppliesToLabels = Object.freeze({
        [BogoAppliesTo.PRODUCT]: "Product-based",
        [BogoAppliesTo.ITEM]: "Item-based",
    });

    const OfferStatus = Object.freeze({
        ACTIVE: "ACTIVE",
        INACTIVE: "INACTIVE",
        EXPIRED: "EXPIRED",
        SCHEDULED: "SCHEDULED",
    });

    const OfferStatusLabels = Object.freeze({
        [OfferStatus.ACTIVE]: "Active",
        [OfferStatus.INACTIVE]: "Inactive",
        [OfferStatus.EXPIRED]: "Expired",
        [OfferStatus.SCHEDULED]: "Scheduled",
    });


    useEffect(() => {
        if (isAdmin !== true) {
            navigate("/profile/details");
        } else {
            setUpdating(true);

            Promise.all([
                getAllDetailsOfProducts(),
                getAllCustomer()
            ])
                .then(([productsResponse, customersResponse]) => {
                    // Handle products response
                    if (productsResponse.data) {

                        setProductData(productsResponse.data);
                        // Extract branch names (MAIN, SEQUOIA, etc.)
                        const branches = Object.keys(productsResponse.data);
                        setAvailableBranches(branches);
                    }

                    setCustomers(customersResponse.customers);

                    // Now load content data AFTER product data is ready
                    if (contentId) {
                        return getContentById(contentId);
                    } else {
                        setUpdating(false);
                        return null;
                    }
                })
                .then((contentResponse) => {
                    if (contentResponse && contentResponse.content) {
                        const content = contentResponse.content;

                        // Set basic fields
                        setTitle(content.title || "");
                        setDescription(content.description || "");
                        setImagePath(content.imagePath || "");
                        setContentLink(content.contentLink || "");
                        setPromotionType(content.promotionType || "");
                        setOfferStatus(content.offerStatus || "ACTIVE");

                        // Set validity dates
                        if (content.validFrom) {
                            setValidFrom(new Date(content.validFrom).toISOString().split('T')[0]);
                        }
                        if (content.validTo) {
                            setValidTo(new Date(content.validTo).toISOString().split('T')[0]);
                        }

                        // Set promotion-type-specific fields
                        if (content.promotionType === PromotionType.DISCOUNT && content.discount) {
                            setDiscountType(content.discount.discountType || "PERCENTAGE");
                            setDiscountValue(content.discount.discountValue?.toString() || "");
                        } else if (content.promotionType === PromotionType.BOGO && content.bogo) {
                            setBogoAppliesTo(content.bogo.appliesTo || "PRODUCT");
                            setBuyQuantity(content.bogo.buyQuantity?.toString() || "");
                            setGetQuantity(content.bogo.getQuantity?.toString() || "");
                            setBogoOfferType(content.bogo.offerType || "FREE");
                            setBogoDiscountValue(content.bogo.discountValue?.toString() || "");
                        } else if (content.promotionType === PromotionType.COUPON && content.coupon) {
                            setCouponCode(content.coupon.couponCode || "");
                            setMinOrderAmount(content.coupon.minOrderAmount?.toString() || "");
                            setMaxDiscountAmount(content.coupon.maxDiscountAmount?.toString() || "");
                            setTimesUsable(content.coupon.timesUsable?.toString() || "");
                            setCouponDescription(content.coupon.couponDescription || "");
                        }

                        // Set customers
                        if (content.customers && Array.isArray(content.customers)) {
                            setSelectedCustomers(content.customers);
                        }

                        // Set branches first - this will trigger loading of brands
                        if (content.branches && Array.isArray(content.branches)) {
                            setSelectedBranches(content.branches);
                        } else if (content.branch && Array.isArray(content.branch)) {
                            // Handle both 'branches' and 'branch' field names
                            setSelectedBranches(content.branch);
                        }

                        // Store remaining selections to be set after cascade populates
                        // We'll use a timeout to allow the cascade effects to run
                        setTimeout(() => {
                            if (content.brands && Array.isArray(content.brands)) {
                                setSelectedBrands(content.brands);
                            }

                            // Set categories, subcategories, phenotypes, and products after brands load
                            setTimeout(() => {
                                if (content.categories && Array.isArray(content.categories)) {
                                    setSelectedCategories(content.categories);
                                }
                                if (content.subCategories && Array.isArray(content.subCategories)) {
                                    setSelectedSubCategories(content.subCategories);
                                } else if (content.menuSubcategories && Array.isArray(content.menuSubcategories)) {
                                    setSelectedSubCategories(content.menuSubcategories);
                                }
                                if (content.phenotypes && Array.isArray(content.phenotypes)) {
                                    setSelectedPhenotypes(content.phenotypes);
                                }
                                if (content.products && Array.isArray(content.products)) {
                                    setSelectedProducts(content.products);
                                }
                            }, 200);
                        }, 200);
                    }
                    setUpdating(false);
                })
                .catch((err) => {
                    console.error("Error loading data:", err);
                    setUpdating(false);
                    if (contentId) {
                        toast.error("Failed to load content data");
                    }
                });
        }
    }, []);

    // Handle branch selection - updates available brands
    useEffect(() => {
        if (selectedBranches.length > 0 && productData) {
            let allBrands = [];

            // Aggregate brands from all selected branches
            selectedBranches.forEach(branchName => {
                const branchData = productData[branchName];
                if (branchData && branchData.brands) {
                    const brands = branchData.brands || [];
                    allBrands = [...allBrands, ...brands];
                }
            });

            // Remove duplicates
            allBrands = [...new Set(allBrands)];
            setAvailableBrands(allBrands);

            // Reset dependent selections when branches change
            setSelectedBrands([]);
            setAvailableCategories([]);
            setAvailableSubCategories([]);
        } else {
            setAvailableBrands([]);
            setSelectedBrands([]);
            setAvailableCategories([]);
            setAvailableSubCategories([]);
        }
    }, [selectedBranches, productData]);

    // Handle brand selection - updates available categories
    useEffect(() => {
        if (selectedBrands.length > 0 && selectedBranches.length > 0 && productData) {
            let allCategories = [];

            // Loop through all selected branches
            selectedBranches.forEach(branchName => {
                const branchData = productData[branchName];
                if (!branchData) return;

                // Loop through selected brands in this branch
                selectedBrands.forEach(brandName => {
                    const brandData = branchData[brandName];
                    if (brandData) {
                        // Extract categories
                        if (brandData.category) {
                            const cats = Array.isArray(brandData.category)
                                ? brandData.category
                                : Object.keys(brandData.category);
                            allCategories = [...new Set([...allCategories, ...cats])];
                        }
                    }
                });
            });

            setAvailableCategories(allCategories);
        } else {
            setAvailableCategories([]);
            setAvailableSubCategories([]);
            setAvailablePhenotypes([]);
            setAvailableProducts([]);
        }
    }, [selectedBrands, selectedBranches, productData]);

    // Handle category selection - updates available subcategories based on selected categories
    useEffect(() => {
        if (selectedCategories.length > 0 && selectedBrands.length > 0 && selectedBranches.length > 0 && productData) {
            let allSubCategories = [];
            let allPhenotypes = [];

            // Loop through all selected branches
            selectedBranches.forEach(branchName => {
                const branchData = productData[branchName];
                if (!branchData) return;

                // Loop through selected brands in this branch
                selectedBrands.forEach(brandName => {
                    const brandData = branchData[brandName];
                    if (brandData) {
                        // Loop through selected categories
                        selectedCategories.forEach(categoryName => {
                            const categoryData = brandData[categoryName];

                            if (categoryData && typeof categoryData === 'object') {
                                // Extract subcategories from this category
                                if (categoryData.menuSubcategories && Array.isArray(categoryData.menuSubcategories)) {
                                    allSubCategories = [...allSubCategories, ...categoryData.menuSubcategories];
                                }
                            }
                        });

                        // Extract phenotypes from brand level
                        if (brandData.phenotypes) {
                            const phenos = Array.isArray(brandData.phenotypes)
                                ? brandData.phenotypes
                                : Object.values(brandData.phenotypes);
                            allPhenotypes = [...new Set([...allPhenotypes, ...phenos])];
                        }
                    }
                });
            });

            // Remove duplicates
            allSubCategories = [...new Set(allSubCategories)];

            setAvailableSubCategories(allSubCategories);
            setAvailablePhenotypes(allPhenotypes);

        } else {
            setAvailableSubCategories([]);
            setAvailableProducts([]);
            setAvailablePhenotypes([]);
        }
    }, [selectedCategories, selectedBrands, selectedBranches, productData]);

    // Handle subcategory selection - filters products based on selected subcategories
    // If no subcategories selected, shows all products from categories
    useEffect(() => {
        if (selectedCategories.length > 0 && selectedBrands.length > 0 && selectedBranches.length > 0 && productData) {
            let allProducts = [];

            // If subcategories are selected, filter by subcategories
            if (selectedSubCategories.length > 0) {
                // Loop through all selected branches
                selectedBranches.forEach(branchName => {
                    const branchData = productData[branchName];
                    if (!branchData) return;

                    // Loop through selected brands in this branch
                    selectedBrands.forEach(brandName => {
                        const brandData = branchData[brandName];
                        if (brandData) {
                            // Loop through selected subcategories
                            selectedSubCategories.forEach(subCategoryName => {
                                const subCategoryData = brandData[subCategoryName];

                                // Check if this subcategory exists as a direct property (like "Old Pal 14g Ready to Roll Kit (12/Case)")
                                if (Array.isArray(subCategoryData)) {
                                    allProducts = [...allProducts, ...subCategoryData];
                                }
                            });
                        }
                    });
                });

            } else {
                // No subcategories selected, show all products from selected categories
                selectedBranches.forEach(branchName => {
                    const branchData = productData[branchName];
                    if (!branchData) return;

                    selectedBrands.forEach(brandName => {
                        const brandData = branchData[brandName];
                        if (brandData) {
                            // Loop through selected categories
                            selectedCategories.forEach(categoryName => {
                                const categoryData = brandData[categoryName];

                                if (categoryData && typeof categoryData === 'object') {
                                    // Extract products from this category
                                    if (categoryData.products && Array.isArray(categoryData.products)) {
                                        allProducts = [...allProducts, ...categoryData.products];
                                    }
                                }
                            });
                        }
                    });
                });

            }

            // Remove duplicate products by _id
            const uniqueProducts = Array.from(
                new Map(allProducts.map(p => [p.inventoryId, p])).values()
            );

            setAvailableProducts(uniqueProducts);
        }
    }, [selectedSubCategories, selectedCategories, selectedBrands, selectedBranches, productData]);

    const handleBranchSelection = (branchName) => {
        const updatedBranches = [...selectedBranches];
        const index = updatedBranches.indexOf(branchName);

        if (index > -1) {
            // Branch is being deselected - remove it and clear all dependent fields
            updatedBranches.splice(index, 1);

            // If no branches left, clear everything below
            if (updatedBranches.length === 0) {
                setSelectedBrands([]);
                setSelectedCategories([]);
                setSelectedSubCategories([]);
                setSelectedPhenotypes([]);
                setSelectedProducts([]);
            }
        } else {
            // Branch is being selected - add it
            updatedBranches.push(branchName);
        }

        setSelectedBranches(updatedBranches);
    };

    const handleBrandSelection = (brandName) => {
        const updatedBrands = [...selectedBrands];
        const index = updatedBrands.indexOf(brandName);

        if (index > -1) {
            // Brand is being deselected - remove it and clear all dependent fields
            updatedBrands.splice(index, 1);

            // Clear categories, subcategories, phenotypes, and products when brand is deselected
            setSelectedCategories([]);
            setSelectedSubCategories([]);
            setSelectedPhenotypes([]);
            setSelectedProducts([]);
        } else {
            // Brand is being selected - add it
            updatedBrands.push(brandName);
        }

        setSelectedBrands(updatedBrands);
    };

    const handleCategorySelection = (category) => {
        const updatedCategories = [...selectedCategories];
        const index = updatedCategories.indexOf(category);

        if (index > -1) {
            // Category is being deselected - remove it and clear dependent fields
            updatedCategories.splice(index, 1);

            // Clear subcategories and products when category is deselected
            setSelectedSubCategories([]);
            setSelectedProducts([]);
        } else {
            // Category is being selected - add it
            updatedCategories.push(category);
        }

        setSelectedCategories(updatedCategories);
    };

    const handleSubCategorySelection = (subCategory) => {
        const updatedSubCategories = [...selectedSubCategories];
        const index = updatedSubCategories.indexOf(subCategory);

        if (index > -1) {
            // Subcategory is being deselected - remove it and clear products
            updatedSubCategories.splice(index, 1);

            // Clear products when subcategory is deselected
            setSelectedProducts([]);
        } else {
            // Subcategory is being selected - add it
            updatedSubCategories.push(subCategory);
        }

        setSelectedSubCategories(updatedSubCategories);
    };

    const handlePhenotypeSelection = (phenotype) => {
        const updatedPhenotypes = [...selectedPhenotypes];
        const index = updatedPhenotypes.indexOf(phenotype);

        if (index > -1) {
            updatedPhenotypes.splice(index, 1);
        } else {
            updatedPhenotypes.push(phenotype);
        }

        setSelectedPhenotypes(updatedPhenotypes);
    };

    const handleProductSelection = (productId) => {
        const updatedProducts = [...selectedProducts];
        const index = updatedProducts.indexOf(productId);

        if (index > -1) {
            updatedProducts.splice(index, 1);
        } else {
            updatedProducts.push(productId);
        }

        setSelectedProducts(updatedProducts);
    };

    const handleSubmit = () => {
        // Basic validation
        if (!title.trim()) {
            toast.error("Title is required");
            return;
        }

        if (!promotionType) {
            toast.error("Promotion type is required");
            return;
        }

        if (selectedBranches.length === 0) {
            toast.error("Please select at least one branch");
            return;
        }

        // Promotion-type-specific validation
        if (promotionType === PromotionType.DISCOUNT) {
            if (!discountValue.trim()) {
                toast.error("Discount value is required");
                return;
            }
            const numValue = parseFloat(discountValue);
            if (isNaN(numValue) || numValue <= 0) {
                toast.error("Discount value must be a positive number");
                return;
            }
        }

        if (promotionType === PromotionType.BOGO) {
            if (![BogoAppliesTo.PRODUCT, BogoAppliesTo.ITEM].includes(bogoAppliesTo)) {
                toast.error("Please select whether this BOGO is product-based or item-based");
                return;
            }
            if (!buyQuantity.trim() || !getQuantity.trim()) {
                toast.error("Buy and Get quantities are required for BOGO");
                return;
            }
            const buyQty = parseInt(buyQuantity);
            const getQty = parseInt(getQuantity);
            if (isNaN(buyQty) || buyQty <= 0 || isNaN(getQty) || getQty <= 0) {
                toast.error("Buy and Get quantities must be positive integers");
                return;
            }
            // Validate discount value for PERCENTAGE_OFF and FLAT_OFF
            if (bogoOfferType === BogoOfferType.PERCENTAGE_OFF || bogoOfferType === BogoOfferType.FLAT_OFF) {
                if (!bogoDiscountValue.trim()) {
                    toast.error(`Discount value is required for ${BogoOfferTypeLabels[bogoOfferType]}`);
                    return;
                }
                const discountVal = parseFloat(bogoDiscountValue);
                if (isNaN(discountVal) || discountVal <= 0) {
                    toast.error("Discount value must be a positive number");
                    return;
                }
                if (bogoOfferType === BogoOfferType.PERCENTAGE_OFF && discountVal > 100) {
                    toast.error("Percentage discount cannot exceed 100%");
                    return;
                }
            }
        }

        if (promotionType === PromotionType.COUPON) {
            if (!couponCode.trim()) {
                toast.error("Coupon code is required");
                return;
            }
            if (!timesUsable.trim()) {
                toast.error("Times usable is required for coupons");
                return;
            }
            const times = parseInt(timesUsable);
            if (isNaN(times) || times <= 0) {
                toast.error("Times usable must be a positive integer");
                return;
            }
        }

        setUpdating(true);

        // Build base content object
        const contentPayload = {
            title: title.trim(),
            description: description.trim(),
            imagePath: imagePath.trim(),
            contentLink: contentLink.trim(),
            branches: selectedBranches,
            brands: selectedBrands,
            categories: selectedCategories,
            subCategories: selectedSubCategories,
            menuSubcategories: selectedSubCategories,
            products: selectedProducts,
            phenotypes: selectedPhenotypes,
            customers: selectedCustomers,
            promotionType: promotionType,
            offerStatus: offerStatus,
        };

        // Add validity dates if provided
        if (validFrom) {
            contentPayload.validFrom = new Date(validFrom).toISOString();
        }
        if (validTo) {
            contentPayload.validTo = new Date(validTo).toISOString();
        }

        // Add promotion-type-specific nested data
        if (promotionType === PromotionType.DISCOUNT) {
            contentPayload.discount = {
                discountType: discountType,
                discountValue: parseFloat(discountValue)
            };
        } else if (promotionType === PromotionType.BOGO) {
            contentPayload.bogo = {
                appliesTo: bogoAppliesTo === BogoAppliesTo.ITEM ? BogoAppliesTo.ITEM : BogoAppliesTo.PRODUCT,
                buyQuantity: parseInt(buyQuantity),
                getQuantity: parseInt(getQuantity),
                offerType: bogoOfferType
            };
            // Add discountValue only if offer type requires it
            if (bogoOfferType === BogoOfferType.PERCENTAGE_OFF || bogoOfferType === BogoOfferType.FLAT_OFF) {
                contentPayload.bogo.discountValue = parseFloat(bogoDiscountValue);
            }
        } else if (promotionType === PromotionType.COUPON) {
            contentPayload.coupon = {
                couponCode: couponCode.trim(),
                timesUsable: parseInt(timesUsable),
                couponDescription: couponDescription.trim()
            };
            if (minOrderAmount.trim()) {
                contentPayload.coupon.minOrderAmount = parseFloat(minOrderAmount);
            }
            if (maxDiscountAmount.trim()) {
                contentPayload.coupon.maxDiscountAmount = parseFloat(maxDiscountAmount);
            }
        }

        if (contentId) {
            contentPayload.id = contentId;
        }

        updateOrCreateContent(contentPayload)
            .then(() => {
                toast.success(contentId ? "Content updated successfully" : "Content created successfully");
                navigate("/profile/contents");
            })
            .catch((err) => {
                toast.error("Failed to save content");
                console.error(err);
            })
            .finally(() => {
                setUpdating(false);
            });
    };

    const handleCancel = () => {
        navigate("/profile/contents");
    };

    // Helper function to get category/phenotype value
    const getValue = (item) => {
        if (typeof item === 'string') {
            return item;
        }

        if (!item || typeof item !== 'object') {
            return '';
        }

        // Try different property names
        const value = item?.name || item?.Name || item?.value || item?.label;

        // If value is still an object, try to extract string from it
        if (value && typeof value === 'object') {
            return value.name || value.Name || value.value || value.label || JSON.stringify(value);
        }

        // If we have a value and it's not an object, convert to string
        if (value !== undefined && value !== null) {
            return String(value);
        }

        // Last resort: try to stringify the item
        return JSON.stringify(item);
    };

    return (
        <Layout>
            {isAdmin === true ? (
                <div className='bg-gray'>
                    <Container className='content-container d-flex'>
                        <Navbar active='content' />
                        <div className='content-wrapper'>
                            <div className='content-section'>
                                <div className='content-header'>
                                    <h3>{contentId ? "Edit Content" : "Create New Content"}</h3>
                                </div>
                                <div className='content-content'>
                                    <Form onSubmit={(e) => e.preventDefault()}>
                                        {/* Basic Info Fields Component */}
                                        <BasicInfoFields
                                            title={title}
                                            setTitle={setTitle}
                                            promotionType={promotionType}
                                            setPromotionType={setPromotionType}
                                            offerStatus={offerStatus}
                                            setOfferStatus={setOfferStatus}
                                            validFrom={validFrom}
                                            setValidFrom={setValidFrom}
                                            validTo={validTo}
                                            setValidTo={setValidTo}
                                            description={description}
                                            setDescription={setDescription}
                                            imagePath={imagePath}
                                            setImagePath={setImagePath}
                                            PromotionType={PromotionType}
                                            PromotionTypeLabels={PromotionTypeLabels}
                                            OfferStatus={OfferStatus}
                                            OfferStatusLabels={OfferStatusLabels}
                                        />

                                        {/* Promotion Type Specific Fields Component */}
                                        <PromotionTypeFields
                                            promotionType={promotionType}
                                            PromotionType={PromotionType}
                                            discountType={discountType}
                                            setDiscountType={setDiscountType}
                                            discountValue={discountValue}
                                            setDiscountValue={setDiscountValue}
                                            DiscountType={DiscountType}
                                            DiscountTypeLabels={DiscountTypeLabels}
                                            bogoAppliesTo={bogoAppliesTo}
                                            setBogoAppliesTo={setBogoAppliesTo}
                                            BogoAppliesTo={BogoAppliesTo}
                                            BogoAppliesToLabels={BogoAppliesToLabels}
                                            buyQuantity={buyQuantity}
                                            setBuyQuantity={setBuyQuantity}
                                            getQuantity={getQuantity}
                                            setGetQuantity={setGetQuantity}
                                            bogoOfferType={bogoOfferType}
                                            setBogoOfferType={setBogoOfferType}
                                            bogoDiscountValue={bogoDiscountValue}
                                            setBogoDiscountValue={setBogoDiscountValue}
                                            BogoOfferType={BogoOfferType}
                                            BogoOfferTypeLabels={BogoOfferTypeLabels}
                                            couponCode={couponCode}
                                            setCouponCode={setCouponCode}
                                            minOrderAmount={minOrderAmount}
                                            setMinOrderAmount={setMinOrderAmount}
                                            maxDiscountAmount={maxDiscountAmount}
                                            setMaxDiscountAmount={setMaxDiscountAmount}
                                            timesUsable={timesUsable}
                                            setTimesUsable={setTimesUsable}
                                            couponDescription={couponDescription}
                                            setCouponDescription={setCouponDescription}
                                        />

                                        {/* Customer Selection Component */}
                                        <CustomerSelection
                                            customers={customers}
                                            selectedCustomers={selectedCustomers}
                                            setSelectedCustomers={setSelectedCustomers}
                                        />

                                        {/* Product Hierarchy Selection Component */}
                                        <ProductHierarchySelection
                                            availableBranches={availableBranches}
                                            selectedBranches={selectedBranches}
                                            handleBranchSelection={handleBranchSelection}
                                            availableBrands={availableBrands}
                                            selectedBrands={selectedBrands}
                                            handleBrandSelection={handleBrandSelection}
                                            availableCategories={availableCategories}
                                            selectedCategories={selectedCategories}
                                            handleCategorySelection={handleCategorySelection}
                                            availableSubCategories={availableSubCategories}
                                            selectedSubCategories={selectedSubCategories}
                                            handleSubCategorySelection={handleSubCategorySelection}
                                            availablePhenotypes={availablePhenotypes}
                                            selectedPhenotypes={selectedPhenotypes}
                                            handlePhenotypeSelection={handlePhenotypeSelection}
                                            availableProducts={availableProducts}
                                            selectedProducts={selectedProducts}
                                            handleProductSelection={handleProductSelection}
                                            getValue={getValue}
                                        />

                                        {/* Action Buttons */}
                                        <Row>
                                            <Col sm={12}>
                                                <div className='d-flex justify-content-end gap-2'>
                                                    <AwareButton
                                                        className='btn btn-secondary'
                                                        onClick={handleCancel}
                                                    >
                                                        Cancel
                                                    </AwareButton>
                                                    <AwareButton
                                                        className='btn btn-primary'
                                                        onClick={handleSubmit}
                                                        disabled={updating}
                                                    >
                                                        {contentId ? "Update" : "Create"} Content
                                                    </AwareButton>
                                                </div>
                                            </Col>
                                        </Row>
                                    </Form>
                                </div>
                            </div>
                        </div>
                    </Container>
                </div>
            ) : null}
            {updating ? (
                <div className='page-loading'>
                    <Spinner animation='border' role='status'>
                        <span className='visually-hidden'>Loading...</span>
                    </Spinner>
                </div>
            ) : null}
        </Layout>
    );
};

export default NewContents;

